import API from "./api";

const ID_FIELDS = ["uan", "pan", "aadhaar"];

let cachedMeta = null;
let cachedKey = null;
let inflight = null;

function isCryptoSubtleAvailable() {
  return (
    typeof window !== "undefined" &&
    window.crypto &&
    window.crypto.subtle &&
    typeof window.crypto.subtle.importKey === "function"
  );
}

function isSecureContextEnough() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const host = window.location?.hostname || "";
  return host === "localhost" || host === "127.0.0.1";
}

function spkiDerBase64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

async function importRsaOaepPublicKey(spkiDerBase64) {
  const keyData = spkiDerBase64ToArrayBuffer(spkiDerBase64);
  return window.crypto.subtle.importKey(
    "spki",
    keyData,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
}

async function rsaOaepEnvelope(publicKey, envelopePrefix, plainText) {
  const enc = new TextEncoder().encode(plainText);
  const ct = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    enc,
  );
  return `${envelopePrefix}${arrayBufferToBase64(ct)}`;
}

async function fetchAndCacheTransportKey() {
  if (cachedKey && cachedMeta) return { publicKey: cachedKey, meta: cachedMeta };
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await API.get("employee/piiTransportKey");
      const body = res?.data || {};
      const meta = body?.status === 1 ? body?.data : null;
      if (!meta?.spkiDerBase64 || !meta?.envelopePrefix) {
        cachedMeta = null;
        cachedKey = null;
        return null;
      }

      const publicKey = await importRsaOaepPublicKey(meta.spkiDerBase64);
      cachedMeta = meta;
      cachedKey = publicKey;
      return { publicKey, meta };
    } catch (_e) {
      cachedMeta = null;
      cachedKey = null;
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

function shouldEncryptValue(v) {
  if (typeof v !== "string") return false;
  const trimmed = v.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("v1:")) return false; // server DB format, not client transport
  if (trimmed.startsWith("rsa:v1:")) return false; // already enveloped
  return true;
}

/**
 * Optionally RSA-encrypt uan/pan/aadhaar in a payload before POST.
 * Falls back to plaintext over HTTPS if transport key isn't available.
 */
export async function maybeEncryptEmployeePiiFields(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (!isCryptoSubtleAvailable() || !isSecureContextEnough()) return payload;

  const hasAny = ID_FIELDS.some((k) => shouldEncryptValue(payload[k]));
  if (!hasAny) return payload;

  const keyBundle = await fetchAndCacheTransportKey();
  if (!keyBundle?.publicKey || !keyBundle?.meta?.envelopePrefix) return payload;

  const { publicKey, meta } = keyBundle;
  const prefix = String(meta.envelopePrefix || "rsa:v1:");

  const next = { ...payload };
  for (const k of ID_FIELDS) {
    const v = next[k];
    if (!shouldEncryptValue(v)) continue;
    next[k] = await rsaOaepEnvelope(publicKey, prefix, v.trim());
  }
  return next;
}

